import { useEffect, useState } from "react";

/**
 * Reads current IST time reliably regardless of the user's local timezone.
 */
function getISTTime() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const val = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
  return { h: val("hour"), m: val("minute"), s: val("second") };
}

/**
 * Returns { remainingSec, status, upcomingSlot }
 * status: "before_start" (before 9 AM), "live" (9 AM–9 PM), "after_end" (after 9 PM)
 * upcomingSlot: "HH:MM" of the next scheduled draw (only meaningful when live).
 * Slots run every 15 min from 09:00 to 21:00 IST.
 */
function computeCountdown() {
  const { h, m, s } = getISTTime();
  const totalSec = h * 3600 + m * 60 + s;
  const startSec = 9 * 3600;
  const endSec = 21 * 3600;

  if (totalSec < startSec) {
    return { remainingSec: startSec - totalSec, status: "before_start", upcomingSlot: "09:00" };
  }
  if (totalSec >= endSec) {
    return { remainingSec: 24 * 3600 - totalSec + startSec, status: "after_end", upcomingSlot: "09:00" };
  }
  const secSinceStart = totalSec - startSec;
  const nextBoundary = Math.ceil((secSinceStart + 1) / (15 * 60)) * (15 * 60);
  const boundaryTotalSec = startSec + nextBoundary;
  const bh = Math.floor(boundaryTotalSec / 3600);
  const bm = Math.floor((boundaryTotalSec % 3600) / 60);
  const upcomingSlot = `${String(bh).padStart(2, "0")}:${String(bm).padStart(2, "0")}`;
  return { remainingSec: nextBoundary - secSinceStart, status: "live", upcomingSlot };
}

/**
 * CountdownTimer — the visible countdown (minutes/seconds/clock/"Next Result In")
 * has been removed at the user's request. Only a small pulsing LIVE indicator
 * remains. Internal timing logic is preserved: it still ticks every second and
 * calls onTick(remainingSec, status, upcomingSlot) so the parent (Home.jsx) can
 * trigger the slot-machine spin animation 60s before each draw, and
 * onDrawReached() when a slot boundary crosses.
 */
export default function CountdownTimer({ onDrawReached, onTick }) {
  const [state, setState] = useState(computeCountdown);

  useEffect(() => {
    let prevRemaining = state.remainingSec;
    if (onTick) onTick(state.remainingSec, state.status, state.upcomingSlot);
    const t = setInterval(() => {
      const next = computeCountdown();
      if (prevRemaining <= 1 && next.remainingSec > prevRemaining && onDrawReached) {
        onDrawReached();
      }
      prevRemaining = next.remainingSec;
      setState(next);
      if (onTick) onTick(next.remainingSec, next.status, next.upcomingSlot);
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLive = state.status === "live";
  const closeToDraw = isLive && state.remainingSec <= 30;

  return (
    <div className="live-indicator" data-testid="live-indicator">
      <span
        className={`live-dot ${closeToDraw ? "live-dot--hot" : ""}`}
        data-testid="live-dot"
      />
      <span className="live-label" data-testid="live-label">
        {isLive ? "LIVE" : "OFFLINE"}
      </span>
    </div>
  );
}
